import type { PlaylistAsset } from '@scio3/shared-types';
import { MIN_DURATION, DEFAULT_DURATION } from './playlist-constants';

/**
 * Minimum allowed duration (seconds) for a playlist item.
 *
 * Documents (PDF/PPTX) must play at least MIN_DURATION (4s) **per page** — the
 * same 4s/page floor the push-to-screen flow enforces (open-api
 * `device.service` sets `documentDuration = durationPage × num_pages`, with
 * `durationPage` floored at 4s). So a 19-page PDF cannot go below 76s. Items
 * with no page count (videos, images, apps) use the flat MIN_DURATION. #1802
 */
export function resolveMinItemDuration(pageCount: number): number {
  return pageCount > 0 ? pageCount * MIN_DURATION : MIN_DURATION;
}

/**
 * Enforce minimum duration for playlist items.
 * Matches SCIO 1 behavior: items with duration < 4s are clamped to 4s.
 *
 * When clamping UP to MIN_DURATION, srcDuration is NOT set (matches SCIO 1's formatAsset.js
 * where srcDuration = duration after enforcement). This means no reset button for short videos.
 *
 * When duration >= MIN_DURATION, srcDuration is set to the original so the reset button
 * can restore it if the user manually changes duration.
 *
 * NOTE (#1892): the `srcDuration` returned here is the *clamped resolved* value, which equals
 * `duration` — it does NOT represent the source asset's own length. Add paths that wipe the
 * stored duration (`shouldIgnoreAppStoredDuration`) must override it with the asset's real
 * duration, or the reset button can never render. See PlaylistDndProvider.resolveSourceDuration.
 */
export function enforceMinDuration(
  rawDuration: number | undefined | null,
  defaultDuration: number = DEFAULT_DURATION,
): {
  duration: number;
  srcDuration?: number;
} {
  const duration = rawDuration ?? defaultDuration;
  if (duration < MIN_DURATION) {
    // Clamp to MIN_DURATION, no srcDuration (reset unavailable for sub-4s sources)
    return { duration: MIN_DURATION };
  }
  // Keep original srcDuration for reset functionality
  return { duration, srcDuration: duration };
}

/**
 * Format duration in seconds to a human-readable string.
 * Always uses "h m s" notation matching scio-2.0.
 * Examples: 90 → "1m 30s", 3661 → "1h 1m 1s", 10 → "10s"
 */
export function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '0s';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * Compact duration for sidebar — single most-significant unit, max 2 digits.
 * Rounds to the nearest unit so the value stays short.
 * Examples: 10 → "10s", 90 → "2m", 3661 → "1h", 10200 → "3h", 7200 → "2h"
 */
export function formatDurationShort(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '0s';

  const hours = Math.round(totalSeconds / 3600);
  const minutes = Math.round(totalSeconds / 60);

  if (hours > 99) {
    const days = Math.round(hours / 24);
    return `${days}d`;
  }
  if (hours >= 1) {
    return `${hours}h`;
  }
  if (minutes >= 1) {
    return `${minutes}m`;
  }
  return `${Math.round(totalSeconds)}s`;
}

/**
 * Format file size in bytes to human-readable string.
 * Examples: 1024 → "1.0 KB", 1048576 → "1.0 MB"
 */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * Calculate total duration of all playlist items.
 * Uses raw precision for accurate duration limit clamping calculations.
 */
export function calculateTotalDuration(items: PlaylistAsset[]): number {
  return items.reduce((total, item) => total + (item.duration ?? 0), 0);
}

/**
 * Calculate total file size of all playlist items.
 */
export function calculateTotalFileSize(items: PlaylistAsset[]): number {
  return items.reduce((total, item) => {
    return total + (item.fileSize ?? 0);
  }, 0);
}
